import * as React from 'react';
import { Download, ExternalLink, Eye, EyeOff, FilePlus2 } from 'lucide-react';
import { DocLang, versionTag } from '@clawhire/shared';
import { formatDateTime } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import type { FileRecord } from '@/components/data/FilesPanel';
import { PdfFileFrame } from '../components/PdfFrame';
import { downloadFile } from '../components/pdf-utils';
import { SectionCard } from '../components/SectionCard';
import type { JdVersion, PositionDetail } from '../types';
import { normalizeChecks } from './jd-form';
import { ChecksList, JdVersionDialog } from './JdVersionDialog';

export interface JdPanelProps {
  position: PositionDetail;
  canEdit: boolean;
  isAdmin: boolean;
}

/** Current JD version card (preview / download / Drive), version history and "New version". */
export function JdPanel({ position, canEdit, isAdmin }: JdPanelProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const files = React.useMemo(() => new Map(position.jdFiles.map((f) => [f.id, f])), [position.jdFiles]);
  const pdfOf = (v: JdVersion): FileRecord | undefined => (v.pdfFileId ? files.get(v.pdfFileId) : undefined);
  const current = position.currentJdVersion;
  const blocked = position.computed.onSiteBlocked;

  const download = async (v: JdVersion) => {
    const pdf = pdfOf(v);
    if (!pdf) return;
    try {
      await downloadFile(pdf.id, pdf.name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download failed');
    }
  };

  return (
    <SectionCard
      title="Job description"
      description={
        position.jdReference
          ? `Reference ${position.jdReference}`
          : 'No JD generated yet – the DC-J reference is assigned with the first version.'
      }
      actions={
        canEdit ? (
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            disabled={blocked && !isAdmin}
            title={blocked && !isAdmin ? 'JD generation is blocked by the on-site rule' : undefined}
          >
            <FilePlus2 aria-hidden="true" /> New version
          </Button>
        ) : null
      }
    >
      {current ? (
        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-medium">
              {current.reference} {versionTag(current.version)}
            </span>
            <EnumBadge def={DocLang} value={current.lang} size="sm" />
            <Badge variant="secondary">
              {current.pageCount ?? '?'} page{current.pageCount === 1 ? '' : 's'}
            </Badge>
            <ChecksBadge version={current} />
            {current.forced ? (
              <Badge variant="destructive" title={current.forceReason ?? undefined}>
                Forced
              </Badge>
            ) : null}
            <Badge variant="outline">current</Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Created {formatDateTime(current.createdAt)}
            {current.createdBy ? ` by ${current.createdBy}` : ''}
            {current.changeNote ? ` · ${current.changeNote}` : ''}
            {current.rateText ? ` · rate text “${current.rateText}”` : ''}
          </p>
          <VersionActions
            version={current}
            pdf={pdfOf(current)}
            previewing={previewId === current.id}
            onPreview={() => setPreviewId(previewId === current.id ? null : current.id)}
            onDownload={() => void download(current)}
          />
          {previewId === current.id && pdfOf(current) ? (
            <PdfFileFrame
              fileId={pdfOf(current)!.id}
              title={`Preview of ${current.reference} ${versionTag(current.version)}`}
            />
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No version yet.</p>
      )}

      {position.jdVersions.length > 0 ? (
        <div className="space-y-1.5">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Version history
          </h4>
          <ol className="divide-y rounded-lg border text-sm" aria-label="JD versions">
            {position.jdVersions.map((v) => (
              <li key={v.id} className="space-y-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono">
                    {v.reference} {versionTag(v.version)}
                  </span>
                  <EnumBadge def={DocLang} value={v.lang} size="sm" />
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(v.createdAt)}
                    {v.pageCount !== null ? ` · ${v.pageCount} p.` : ''}
                    {v.changeNote ? ` · ${v.changeNote}` : ''}
                  </span>
                  {v.id === position.currentJdVersionId ? (
                    <Badge variant="outline" className="text-[10px]">
                      current
                    </Badge>
                  ) : null}
                  <div className="ml-auto">
                    <VersionActions
                      version={v}
                      pdf={pdfOf(v)}
                      compact
                      previewing={previewId === v.id}
                      onPreview={() => setPreviewId(previewId === v.id ? null : v.id)}
                      onDownload={() => void download(v)}
                    />
                  </div>
                </div>
                {previewId === v.id && v.id !== current?.id && pdfOf(v) ? (
                  <PdfFileFrame
                    fileId={pdfOf(v)!.id}
                    title={`Preview of ${v.reference} ${versionTag(v.version)}`}
                  />
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <JdVersionDialog open={dialogOpen} onOpenChange={setDialogOpen} position={position} isAdmin={isAdmin} />
    </SectionCard>
  );
}

function ChecksBadge({ version }: { version: JdVersion }) {
  const [open, setOpen] = React.useState(false);
  const summary = normalizeChecks(version.checkResults);
  const label =
    summary.errors.length === 0 && summary.warnings.length === 0
      ? 'checks OK'
      : `${summary.errors.length} error${summary.errors.length === 1 ? '' : 's'}, ${summary.warnings.length} warning${summary.warnings.length === 1 ? '' : 's'}`;
  return (
    <>
      <button
        type="button"
        className="inline-flex"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Show check results"
      >
        <Badge
          variant={summary.errors.length ? 'destructive' : summary.warnings.length ? 'secondary' : 'muted'}
        >
          {label}
        </Badge>
      </button>
      {open ? (
        <div className="basis-full">
          <ChecksList title="Check results" summary={summary} />
        </div>
      ) : null}
    </>
  );
}

function VersionActions({
  version,
  pdf,
  previewing,
  onPreview,
  onDownload,
  compact = false,
}: {
  version: JdVersion;
  pdf: FileRecord | undefined;
  previewing: boolean;
  onPreview: () => void;
  onDownload: () => void;
  compact?: boolean;
}) {
  const tag = `${version.reference} ${versionTag(version.version)}`;
  const size = compact ? 'icon-sm' : 'sm';
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Button
        variant={compact ? 'ghost' : 'outline'}
        size={size}
        disabled={!pdf}
        aria-pressed={previewing}
        aria-label={compact ? `${previewing ? 'Hide preview of' : 'Preview'} ${tag}` : undefined}
        onClick={onPreview}
      >
        {previewing ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
        {compact ? null : previewing ? 'Hide preview' : 'Preview'}
      </Button>
      <Button
        variant={compact ? 'ghost' : 'outline'}
        size={size}
        disabled={!pdf}
        aria-label={compact ? `Download ${tag}` : undefined}
        onClick={onDownload}
      >
        <Download aria-hidden="true" />
        {compact ? null : 'Download'}
      </Button>
      {pdf?.webViewLink ? (
        <Button asChild variant={compact ? 'ghost' : 'outline'} size={size}>
          <a
            href={pdf.webViewLink}
            target="_blank"
            rel="noreferrer"
            aria-label={compact ? `Open ${tag} in Drive` : undefined}
          >
            <ExternalLink aria-hidden="true" />
            {compact ? null : 'Open in Drive'}
          </a>
        </Button>
      ) : null}
    </div>
  );
}
